"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
} from "react";
import { io, Socket } from "socket.io-client";
import { useAuth, useUser } from "@clerk/nextjs";

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({ socket: null, isConnected: false });

export function SocketProvider({ children }: { children: ReactNode }) {
  const { user, isSignedIn, isLoaded } = useUser();
  const { sessionId } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      return;
    }

    if (!user?.publicMetadata) {
      console.warn("⚠️ Sesión no válida o incompleta");
      return;
    }

    const agentInfo = user.publicMetadata;
    const socketServer = process.env.NEXT_PUBLIC_SOCKET_URL;

    if (!socketServer) {
      console.error("⚠️ URL del servidor de sockets no definida");
      return;
    }

    console.log("🔗 Conectando al servidor de sockets...");

    const newSocket = io(socketServer, {
      withCredentials: true,
      transports: ["websocket"],
      query: { session: JSON.stringify(agentInfo) },
      auth: { agentInfo, sessionId },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      console.log("✅ Conectado al servidor de sockets");
      newSocket.emit("join room", agentInfo?.client);
      console.log(`🔗 Intentando unirse a la sala: ${agentInfo?.client}`);
      setSocket(newSocket); // 🔹 Esto dispara re-render en consumidores
      setIsConnected(true); // 🔹 Marcar como conectado
    });

    newSocket.on("disconnect", (reason) => {
      console.warn("❌ Desconectado del servidor de sockets:", reason);
      setIsConnected(false); // 🔹 Marcar como desconectado
    });

    newSocket.on("connect_error", (error) => {
      console.error("⚠️ Error de conexión:", error.message);
      setIsConnected(false); // 🔹 Marcar como desconectado en error
    });

    newSocket.on("reconnect_attempt", (attempt) => {
      console.log(`🔄 Intentando reconectar... Intento #${attempt}`);
    });

    newSocket.on("reconnect_failed", () => {
      console.error("🚨 No se pudo reconectar después de varios intentos.");
      setIsConnected(false); // 🔹 Marcar como desconectado si falla reconexión
    });

    return () => {
      console.log("🔌 Cerrando conexión de socket");
      newSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    };
  }, [isLoaded, isSignedIn, user?.id, sessionId]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
